/* ===================================================================
   Agency / Admin view -- LIVE cross-tenant operator dashboard (CORBATO,
   2026-06-18). Rewired from the Claude Design mock to real api/v1/admin data.

   GATING: this whole route is admin-only. It calls getAdminMe() first; a
   non-admin (the decorator returns 403) sees an "unauthorized" state and no
   data ever renders. A normal client must never land here with data.

   DATA: live from lib/api/admin (GET /admin/me, /admin/tenants,
   /admin/tenants/:id). Every figure is real; empty/zero states are honest
   (no fabricated numbers). The 30-day sparkline is COSMETIC (no real per-day
   series endpoint yet) and is rendered from the tenant's own rollup numbers
   as a deterministic placeholder, clearly a visual only.

   READ-ONLY (Hard Rule #1): nothing here posts, approves, or sends. The
   tenant drawer shows reviews/queue/health for observation only -- there is
   NO approve/post button. "Add client" stays a stub (self-service signup is
   Hard Rule #9).
   =================================================================== */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardHead, Chip, Icon, Stars, Stat, PlusPath, SkeletonStats } from '../components/ui';
import { LogoutButton } from '../components/LogoutButton';
import { api, ApiError, type AdminHealth, type AdminMetrics } from '../lib/api';
import '../styles/components.css';

type OperatorTab = 'tenants' | 'reviews' | 'queue';

const HEALTH_LABEL: Record<AdminHealth, string> = {
  good: 'Healthy',
  warn: 'Watch',
  bad: 'At risk',
};

/* Deterministic sparkline bars. COSMETIC ONLY -- there is no real 30-day
   series endpoint yet, so this is a stable visual seeded off the tenant's own
   numbers, not a data claim. */
function Spark({ seed }: { seed: number }) {
  const bars = Array.from({ length: 10 }, (_, i) => 6 + ((seed * 7 + i * i * 3) % 18));
  return (
    <span className="spark" aria-hidden="true">
      {bars.map((h, i) => (
        <i key={i} style={{ height: h }} />
      ))}
    </span>
  );
}

/* ONE PLAN (Steve, 2026-08-06): every subscriber is on the same $59 plan, so
   the stored tier value no longer selects a label -- it only tells us whether
   the tenant has a plan at all.

   This ALSO fixes a latent bug. The old version compared against 'starter',
   which the engine never stores (it stores 'basic'), so every ordinary tenant
   fell through to `return tier` and this column rendered a raw lowercase
   "basic" instead of a plan name. */
function planLabel(tier: string | null): string {
  return (tier || '').trim() ? 'Rhody Reviews' : '-';
}

function avatarColor(name: string): string {
  // Stable color from the business name (visual only).
  const palette = ['#2A6FDB', '#1F8A5B', '#7A3FB5', '#B5532A', '#2A8FA0', '#C8924C', '#3F6FB5', '#8A6BB5'];
  let h = 0;
  for (let i = 0; i < name.length; i += 1) h = (h * 31 + name.charCodeAt(i)) % 997;
  return palette[h % palette.length];
}

export function Agency() {
  const navigate = useNavigate();
  const [openClientId, setOpenClientId] = useState<string | null>(null);
  const [tab, setTab] = useState<OperatorTab>('tenants');

  // 1. Gate: is the signed-in user an operator? A 403 -> not an admin.
  const meQ = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: ({ signal }) => api.getAdminMe(signal),
    retry: false,
    staleTime: 5 * 60_000,
  });

  const isAdmin = meQ.data?.is_admin === true;

  // 2. Tenants -- only fetched once admin-ness is confirmed.
  const tenantsQ = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: ({ signal }) => api.listTenants(signal),
    enabled: isAdmin,
    staleTime: 60_000,
  });

  // ----- Gate states -----
  if (meQ.isLoading) {
    return <CenterNote title="Loading..." body="Checking your admin access." />;
  }
  const unauthorized =
    meQ.isError && meQ.error instanceof ApiError && (meQ.error.status === 403 || meQ.error.status === 401);
  if (unauthorized || (meQ.isSuccess && !isAdmin)) {
    return (
      <CenterNote
        title="Not available"
        body="This area is for Rhody Reviews operators only. If you reached this by mistake, head back to your dashboard."
      />
    );
  }
  if (meQ.isError) {
    return (
      <CenterNote
        title="Could not load admin"
        body="There was a problem checking access. Please try again."
      />
    );
  }

  const tenants = tenantsQ.data ?? [];

  // KPI rollup computed from the live tenants array (NO fabricated revenue).
  const activeCount = tenants.filter((t) => (t.status || '').toLowerCase() === 'active').length;
  const rated = tenants.filter((t) => typeof t.avg_rating === 'number');
  const avgRating =
    rated.length > 0
      ? (rated.reduce((s, t) => s + (t.avg_rating as number), 0) / rated.length).toFixed(1)
      : '--';
  const reviews30 = tenants.reduce((s, t) => s + (t.reviews_30d || 0), 0);
  const pendingDrafts = tenants.reduce((s, t) => s + (t.pending_drafts || 0), 0);

  function addClient() {
    /* Stub: self-service signup is Hard Rule #9. Operators do not hand-create
       tenants here. No-op until/unless a dogfood-seed flow is specced. */
  }

  return (
    <div className="awrap">
      {/* Operator top bar: this is the Rhody Reviews platform operator console,
          NOT a white-label agency view. Steve is the platform operator. */}
      <div className="wlbar">
        <span className="brandmark" style={{ width: 30, height: 30, borderRadius: 8 }}>
          <svg viewBox="0 0 16 16" fill="#fff" width={13} height={13}>
            <path d="M8 1.5l2 4.5 5 .5-3.7 3.4 1 4.9L8 12l-4.3 2.8 1-4.9L1 6.5 6 6z" />
          </svg>
        </span>
        <div className="nm">Rhody Reviews</div>
        <span className="wl">Operator console</span>
        <div className="me">
          <span className="role">Operator</span>
          <span className="ava">RR</span>
          {/* Log out of the operator console -> back to /auth. */}
          <LogoutButton variant="bar" />
        </div>
      </div>

      <div className="ahead">
        <h1>Operator console</h1>
        <p>All tenants on the platform, in one place. A read-only overview to spot which businesses need attention. Tenants run their own dashboards.</p>
      </div>

      {/* KPI rollup -- computed live from the tenants book. */}
      <div className="row kpis" style={{ marginBottom: 20 }}>
        <Kpi lab="Active clients" num={String(activeCount)} delta={`${tenants.length} total`} />
        <Kpi lab="Avg rating" num={avgRating} delta="across book" />
        <Kpi lab="Reviews collected" num={String(reviews30)} delta="last 30 days" />
        <Kpi lab="Drafts waiting" num={String(pendingDrafts)} delta="pending approval" />
      </div>

      {/* Operator tabs: tenants table + the two cross-tenant read-only feeds. */}
      <div className="optabs" role="tablist" aria-label="Operator views">
        <button
          role="tab"
          aria-selected={tab === 'tenants'}
          className={'optab' + (tab === 'tenants' ? ' is-active' : '')}
          onClick={() => setTab('tenants')}
        >
          Tenants
        </button>
        <button
          role="tab"
          aria-selected={tab === 'reviews'}
          className={'optab' + (tab === 'reviews' ? ' is-active' : '')}
          onClick={() => setTab('reviews')}
        >
          Recent reviews
        </button>
        <button
          role="tab"
          aria-selected={tab === 'queue'}
          className={'optab' + (tab === 'queue' ? ' is-active' : '')}
          onClick={() => setTab('queue')}
        >
          Pending queue
        </button>
      </div>

      {tab === 'reviews' && <ReviewsFeed />}
      {tab === 'queue' && <QueueFeed />}

      {tab === 'tenants' && (
      <>
      {/* Business metrics -- local list-price SaaS numbers (GET /admin/metrics). */}
      <BusinessMetricsPanel />

      <div className="vhead">
        <h2>Clients</h2>
        <button className="btn btn--primary" onClick={addClient} disabled title="Self-service signup only">
          <Icon d={PlusPath} sw={2.2} /> Add client
        </button>
      </div>

      <div className="card" style={{ padding: '8px 6px' }}>
        {tenantsQ.isLoading ? (
          <p style={{ padding: 24, color: 'var(--ink-soft, #667)' }}>Loading clients...</p>
        ) : tenantsQ.isError ? (
          <p style={{ padding: 24, color: 'var(--ink-soft, #667)' }}>Could not load clients. Please try again.</p>
        ) : tenants.length === 0 ? (
          <p style={{ padding: 24, color: 'var(--ink-soft, #667)' }}>
            No client tenants yet. They appear here as businesses sign up.
          </p>
        ) : (
          <table className="tbl tbl--stack">
            <thead>
              <tr>
                <th>Business</th>
                <th>Plan</th>
                <th>Rating</th>
                <th>Reviews &middot; 30d</th>
                <th title="Illustrative shape only -- no real per-day series yet.">
                  30-day trend <span className="adm-illus">illustrative</span>
                </th>
                <th title="Derived from rating + activity; a rough indicator, not a real health score.">
                  Health <span className="adm-illus">rough</span>
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tenants.map((c) => (
                <tr key={c.client_id}>
                  <td data-label="Business">
                    <div className="who">
                      <span className="ava" style={{ background: avatarColor(c.business_name || c.client_id) }}>
                        {(c.business_name || c.client_id)[0]?.toUpperCase()}
                      </span>
                      <span>{c.business_name || c.client_id}</span>
                    </div>
                  </td>
                  <td data-label="Plan">
                    <Chip kind={c.tier ? 'blue' : 'gray'}>{planLabel(c.tier)}</Chip>
                  </td>
                  <td data-label="Rating">
                    {typeof c.avg_rating === 'number' ? (
                      <span style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {c.avg_rating.toFixed(1)} <Stars n={Math.round(c.avg_rating)} />
                      </span>
                    ) : (
                      <span style={{ color: 'var(--ink-soft, #889)' }}>No reviews yet</span>
                    )}
                  </td>
                  <td data-label="Reviews 30d">
                    <Chip kind="green">{`+${c.reviews_30d}`}</Chip>
                  </td>
                  <td data-label="30-day trend">
                    <Spark seed={(c.reviews_30d || 0) + (c.total_reviews || 0)} />
                  </td>
                  <td data-label="Health">
                    <span className={'health ' + c.health}>{HEALTH_LABEL[c.health]}</span>
                  </td>
                  <td data-label="" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {/* Primary: open this client's FULL dashboard read-only
                        (Option A). Secondary "Quick look" keeps the lightweight
                        drawer rollup. */}
                    <button
                      className="mini mini--go"
                      style={{ marginRight: 6 }}
                      onClick={() => navigate(`/agency/tenant/${encodeURIComponent(c.client_id)}`)}
                    >
                      View dashboard
                    </button>
                    <button className="mini mini--ghost" onClick={() => setOpenClientId(c.client_id)}>
                      Quick look
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </>
      )}

      {openClientId && (
        <TenantDrawer clientId={openClientId} onClose={() => setOpenClientId(null)} />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
   BUSINESS METRICS PANEL (Dashboard KB Phase 2, Brad 2026-06-30).
   Surfaces GET /admin/metrics -- LOCAL, list-price SaaS planning numbers
   computed from the clients table only (NO Stripe call). Every revenue-shaped
   figure is honestly labeled "list-price basis" per the endpoint's `note`. When
   stripe_verified.available is false, a small "Stripe-verified MRR pending" line
   shows rather than hiding the section. Redundant encoding throughout (status
   chips carry a dot + a number + a word; never color alone). READ-ONLY.
   --------------------------------------------------------------------------- */
const STATUS_META: { key: keyof AdminMetrics['status_counts']; label: string; color: string }[] = [
  { key: 'active', label: 'Active', color: 'var(--green)' },
  { key: 'trial', label: 'Trial', color: 'var(--accent)' },
  { key: 'paused', label: 'Paused', color: 'var(--gold)' },
  { key: 'canceled', label: 'Canceled', color: 'var(--red)' },
];

function CohortChart({ rows }: { rows: AdminMetrics['signups_by_month'] }) {
  if (rows.length === 0) {
    return <div className="chart-empty" style={{ minHeight: 90 }}>No dated signups yet.</div>;
  }
  const max = Math.max(1, ...rows.map((r) => r.signups));
  return (
    <div className="adm-cohort">
      {rows.map((r) => (
        <div className="col" key={r.month} title={`${r.month}: ${r.signups} signup${r.signups === 1 ? '' : 's'}`}>
          <div className="bar" style={{ height: Math.max(3, Math.round((r.signups / max) * 100)) + '%' }} />
          <span>{r.month.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}

function BusinessMetricsPanel() {
  const q = useQuery({
    queryKey: ['admin', 'metrics'],
    queryFn: ({ signal }) => api.getAdminMetrics(signal),
    staleTime: 60_000,
    retry: false,
  });

  const m = q.data;

  return (
    <Card className="adm-metrics" style={{ padding: 20, marginBottom: 20 }}>
      <CardHead
        title="Business metrics"
        right={<span className="adm-basis">List-price basis</span>}
      />
      {q.isLoading ? (
        <SkeletonStats rows={3} />
      ) : q.isError || !m ? (
        <p style={{ color: 'var(--ink-soft, #667)', fontSize: 13, padding: '6px 0' }}>
          Could not load business metrics. Please try again.
        </p>
      ) : (
        <>
          <p className="adm-note">{m.note}</p>

          {/* Client counts by status -- dot + number + word (redundant encoding). */}
          <div className="adm-statuschips">
            {STATUS_META.map((s) => (
              <span className="adm-statuschip" key={s.key}>
                <span className="dot" style={{ background: s.color }} aria-hidden="true" />
                <b>{m.status_counts[s.key]}</b> {s.label}
              </span>
            ))}
            <span className="adm-statuschip">
              <b>{m.clients_total}</b> Total
            </span>
          </div>

          {/* Top-line list-price KPIs. */}
          <div className="row kpis" style={{ margin: '14px 0 4px' }}>
            <Kpi
              lab="ARPU (list price)"
              num={m.arpu.value_usd != null ? `$${m.arpu.value_usd.toLocaleString()}` : '--'}
              delta={`${m.arpu.active_clients} active`}
            />
            <Kpi
              lab="List-price MRR"
              num={`$${m.arpu.list_price_mrr_usd.toLocaleString()}`}
              delta="active tiers x list price"
            />
            <Kpi
              lab="Trial to paid"
              num={m.trial_to_paid.value_pct != null ? `${m.trial_to_paid.value_pct}%` : '--'}
              delta={`${m.trial_to_paid.active} paid / ${m.trial_to_paid.active + m.trial_to_paid.canceled} past trial (approx)`}
            />
            <Kpi
              lab="Tiers"
              num={String(m.tier_mix.reduce((s, t) => s + t.count, 0))}
              delta={m.tier_mix.map((t) => `${t.count} ${t.display}`).join(' / ') || 'no tiers yet'}
            />
          </div>

          <div className="row split" style={{ marginTop: 14 }}>
            <Card>
              <CardHead title="Signups by month" />
              <CohortChart rows={m.signups_by_month} />
            </Card>
            <Card>
              <CardHead title="Tier mix" />
              {m.tier_mix.length === 0 ? (
                <div className="chart-empty" style={{ textAlign: 'left' }}>No tiers yet.</div>
              ) : (
                m.tier_mix.map((t) => {
                  const total = m.tier_mix.reduce((s, x) => s + x.count, 0) || 1;
                  return (
                    <Stat
                      key={t.tier}
                      name={t.display}
                      value={t.count.toLocaleString()}
                      pct={Math.round((t.count / total) * 100)}
                    />
                  );
                })
              )}
            </Card>
          </div>

          {/* Stripe-verified collected revenue: honest pending line until a
              read-only Stripe snapshot exists (Phase 3). */}
          {!m.stripe_verified.available && (
            /* A3: clean one-liner. The raw stripe_verified.reason (migration /
               key-scope runbook text) is intentionally NOT rendered in the UI. */
            <div className="adm-stripe-pending">
              <span className="dot" aria-hidden="true" />
              <span>
                The figures above are list-price planning numbers, not Stripe-verified collected revenue.
              </span>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/* ---------------------------------------------------------------------------
   Cross-tenant RECENT REVIEWS feed (GET /admin/reviews). READ-ONLY: this is an
   operator "what is happening right now across the platform" view. Each row is
   tagged with the tenant business_name + source + rating + needs-reply. The
   endpoint's filters (client_id, needs_reply) are surfaced as simple controls.
   There is deliberately NO approve/reply/post control here -- posting stays in
   each tenant's own approval loop (Hard Rule #1).

   SOURCE FILTER REMOVED (2026-08-06, Steve). This had a Platform dropdown
   offering Google / Facebook / Yelp. Review INGESTION is Google-only and has
   been since 2026-06-28 -- monitor_agent._sync_platform hard-refuses yelp and
   facebook regardless of config, and baseline_import returns before any Yelp
   or Facebook call. So two of the three options could only ever return zero
   rows while implying Rhody Reviews pulls reviews from those directories. It
   does not. With Google the only possible source the whole control was dead
   UI, so it is gone rather than reduced to a one-option dropdown.

   Findable on a directory (Listings/NAP) is a different thing and stays
   multi-directory. This is about where reviews are INGESTED FROM.
   --------------------------------------------------------------------------- */
function ReviewsFeed() {
  const [needsReply, setNeedsReply] = useState<boolean>(false);
  const [clientId, setClientId] = useState<string>('');

  const q = useQuery({
    queryKey: ['admin', 'reviews', { needsReply, clientId }],
    queryFn: ({ signal }) =>
      api.listAdminReviews(
        {
          needs_reply: needsReply || undefined,
          client_id: clientId.trim() || undefined,
          limit: 100,
        },
        signal,
      ),
    staleTime: 30_000,
  });

  const rows = q.data ?? [];

  return (
    <>
      <div className="vhead">
        <h2>Recent reviews</h2>
        <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Across all tenants. Read-only.</span>
      </div>

      <div className="card" style={{ padding: 14, marginBottom: 14, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ display: 'inline-flex', flexDirection: 'column', gap: 4, fontSize: 12.5, color: 'var(--muted)' }}>
          Tenant client_id
          <input
            type="text"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="(all tenants)"
            className="opfilter"
            style={{ minWidth: 220 }}
          />
        </label>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--ink-2)', alignSelf: 'flex-end', paddingBottom: 6 }}>
          <input type="checkbox" checked={needsReply} onChange={(e) => setNeedsReply(e.target.checked)} />
          Needs a reply only
        </label>
      </div>

      <div className="card" style={{ padding: '8px 6px' }}>
        {q.isLoading ? (
          <p style={{ padding: 24, color: 'var(--ink-soft, #667)' }}>Loading reviews...</p>
        ) : q.isError ? (
          <p style={{ padding: 24, color: 'var(--ink-soft, #667)' }}>Could not load reviews. Please try again.</p>
        ) : rows.length === 0 ? (
          <p style={{ padding: 24, color: 'var(--ink-soft, #667)' }}>
            No reviews match these filters yet. Reviews appear here as tenants collect them.
          </p>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Business</th>
                <th>Platform</th>
                <th>Rating</th>
                <th>Reviewer</th>
                <th>Review</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className="who">
                      <span className="ava" style={{ background: avatarColor(r.business_name || r.client_id) }}>
                        {(r.business_name || r.client_id)[0]?.toUpperCase()}
                      </span>
                      <span>{r.business_name || r.client_id}</span>
                    </div>
                  </td>
                  <td>
                    <Chip kind="gray">{r.platform || '-'}</Chip>
                  </td>
                  <td>
                    {typeof r.rating === 'number' ? (
                      <span style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {r.rating} <Stars n={Math.round(r.rating)} />
                      </span>
                    ) : (
                      <span style={{ color: 'var(--ink-soft, #889)' }}>-</span>
                    )}
                  </td>
                  <td>{r.reviewer_name || 'Anonymous'}</td>
                  <td style={{ maxWidth: 360, color: 'var(--muted-2)' }}>
                    {(r.review_text || '').slice(0, 160) || <span style={{ color: 'var(--ink-soft, #889)' }}>No text</span>}
                  </td>
                  <td>
                    {r.reply_timestamp ? (
                      <Chip kind="green">Replied</Chip>
                    ) : (
                      <Chip kind="gold">Needs reply</Chip>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

/* ---------------------------------------------------------------------------
   Cross-tenant PENDING-APPROVAL QUEUE feed (GET /admin/queue). READ-ONLY: shows
   which tenants have drafts waiting in their own approval loop, each tagged with
   the tenant + the linked review summary. The operator can SEE the backlog but
   cannot approve/post from here -- approval stays with the business (Hard Rule
   #1). No filters: the endpoint takes only a bounded limit.
   --------------------------------------------------------------------------- */
function QueueFeed() {
  const q = useQuery({
    queryKey: ['admin', 'queue'],
    queryFn: ({ signal }) => api.listAdminQueue({ limit: 100 }, signal),
    staleTime: 30_000,
  });

  const rows = q.data ?? [];

  return (
    <>
      <div className="vhead">
        <h2>Pending approval queue</h2>
        <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>Drafts waiting in tenant queues. Read-only.</span>
      </div>

      <div className="card" style={{ padding: '8px 6px' }}>
        {q.isLoading ? (
          <p style={{ padding: 24, color: 'var(--ink-soft, #667)' }}>Loading queue...</p>
        ) : q.isError ? (
          <p style={{ padding: 24, color: 'var(--ink-soft, #667)' }}>Could not load the queue. Please try again.</p>
        ) : rows.length === 0 ? (
          <p style={{ padding: 24, color: 'var(--ink-soft, #667)' }}>
            No drafts are waiting for approval right now. Tenant drafts appear here while pending.
          </p>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Business</th>
                <th>Review</th>
                <th>Draft reply</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => (
                <tr key={item.draft_id}>
                  <td>
                    <div className="who">
                      <span className="ava" style={{ background: avatarColor(item.business_name || item.client_id) }}>
                        {(item.business_name || item.client_id)[0]?.toUpperCase()}
                      </span>
                      <span>{item.business_name || item.client_id}</span>
                    </div>
                  </td>
                  <td style={{ maxWidth: 280 }}>
                    {item.review ? (
                      <>
                        <span style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {typeof item.review.rating === 'number' ? (
                            <>
                              {item.review.rating} <Stars n={Math.round(item.review.rating)} />
                            </>
                          ) : null}
                          <span className="sub">{item.review.platform || ''}</span>
                        </span>
                        <div style={{ color: 'var(--muted-2)', fontSize: 12.5, marginTop: 3 }}>
                          {(item.review.review_text || '').slice(0, 120)}
                        </div>
                      </>
                    ) : (
                      <span style={{ color: 'var(--ink-soft, #889)' }}>No linked review</span>
                    )}
                  </td>
                  <td style={{ maxWidth: 320, color: 'var(--muted-2)' }}>
                    {(item.draft_response || '').slice(0, 160) || (
                      <span style={{ color: 'var(--ink-soft, #889)' }}>No draft text</span>
                    )}
                  </td>
                  <td>
                    {item.escalated ? <Chip kind="gold">Escalated</Chip> : <Chip kind="gray">{item.status}</Chip>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function Kpi({ lab, num, delta }: { lab: string; num: string; delta: string }) {
  return (
    <div className="card kpi">
      <div className="lab">{lab}</div>
      <div className="num">{num}</div>
      <div className="delta">{delta}</div>
    </div>
  );
}

function CenterNote({ title, body }: { title: string; body: string }) {
  return (
    <div className="awrap">
      <div className="card" style={{ maxWidth: 520, margin: '64px auto', padding: 28, textAlign: 'center' }}>
        <h2 style={{ marginTop: 0 }}>{title}</h2>
        <p style={{ color: 'var(--ink-soft, #667)' }}>{body}</p>
      </div>
    </div>
  );
}

/* Read-only tenant drill-in. Shows reviews / runs / funnel / health for one
   tenant. NO approve/post button anywhere (Hard Rule #1). */
function TenantDrawer({ clientId, onClose }: { clientId: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['admin', 'tenant', clientId],
    queryFn: ({ signal }) => api.getTenant(clientId, signal),
    staleTime: 30_000,
  });
  const t = q.data;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(20,28,40,0.45)',
        display: 'flex', justifyContent: 'flex-end', zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 92vw)', height: '100%', background: 'var(--surface, #fff)',
          overflowY: 'auto', padding: 24, boxShadow: '-8px 0 24px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>{t?.business_name || clientId}</h2>
          <button className="mini mini--ghost" onClick={onClose}>Close</button>
        </div>

        {q.isLoading ? (
          <p style={{ color: 'var(--ink-soft, #667)' }}>Loading tenant...</p>
        ) : q.isError ? (
          <p style={{ color: 'var(--ink-soft, #667)' }}>Could not load this tenant.</p>
        ) : t ? (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' }}>
              <Chip kind={t.tier ? 'blue' : 'gray'}>{planLabel(t.tier)}</Chip>
              <span className={'health ' + t.health}>{HEALTH_LABEL[t.health]}</span>
              <Chip kind="gray">{t.status || 'unknown'}</Chip>
            </div>

            <div className="row kpis" style={{ margin: '8px 0 16px' }}>
              <Kpi lab="Total reviews" num={String(t.total_reviews)} delta="all time" />
              <Kpi
                lab="Avg rating"
                num={typeof t.avg_rating === 'number' ? t.avg_rating.toFixed(1) : '--'}
                delta="across reviews"
              />
              <Kpi lab="Unanswered" num={String(t.unanswered_count)} delta="need a reply" />
              <Kpi lab="Drafts waiting" num={String(t.pending_drafts)} delta="pending approval" />
            </div>

            <h3>Request funnel &middot; 30d</h3>
            <p style={{ color: 'var(--ink-soft, #667)' }}>
              Sent {t.request_funnel.sent} &middot; Delivered/opened {t.request_funnel.delivered_or_opened} &middot;
              {' '}Left review {t.request_funnel.left_review}
            </p>

            <h3>Recent reviews</h3>
            {t.recent_reviews.length === 0 ? (
              <p style={{ color: 'var(--ink-soft, #889)' }}>No reviews yet.</p>
            ) : (
              <ul style={{ paddingLeft: 18 }}>
                {t.recent_reviews.slice(0, 8).map((r) => (
                  <li key={r.id} style={{ marginBottom: 8 }}>
                    <strong>{r.rating ?? '-'}&#9733;</strong> {r.reviewer_name || 'Anonymous'}
                    {r.reply_timestamp ? ' (replied)' : ' (needs reply)'}
                    <div style={{ color: 'var(--ink-soft, #667)', fontSize: 13 }}>
                      {(r.review_text || '').slice(0, 140)}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <h3>Agent runs</h3>
            {t.recent_runs.length === 0 ? (
              <p style={{ color: 'var(--ink-soft, #889)' }}>No recent runs.</p>
            ) : (
              <ul style={{ paddingLeft: 18 }}>
                {t.recent_runs.slice(0, 6).map((run) => (
                  <li key={run.id} style={{ marginBottom: 4 }}>
                    {run.agent_name || 'agent'} &mdash; {run.status || 'unknown'}{' '}
                    <span style={{ color: 'var(--ink-soft, #889)', fontSize: 12 }}>{run.started_at}</span>
                  </li>
                ))}
              </ul>
            )}

            <h3>Integrations</h3>
            <p style={{ color: 'var(--ink-soft, #667)' }}>
              Twilio number {t.presence.twilio_number_configured ? 'configured' : 'not set'} &middot;
              {' '}Google {t.presence.google_oauth_connected ? 'OAuth token present' : 'no token'} &middot;
              {' '}Stripe {t.presence.stripe_customer_linked ? 'linked' : 'not linked'}
            </p>
            <p style={{ color: 'var(--ink-soft, #889)', fontSize: 12 }}>
              Read-only view. Replies are approved and posted by the business in its own dashboard.
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}
